// WGSL sources are imported as raw text (Vite `?raw`) and registered with the
// shader library at context creation. Keeping them as real `.wgsl` files rather
// than template literals means editors syntax-highlight them and the shader test
// can parse every one of them without a GPU.

import alphafillPull from './alphafill_pull.wgsl?raw';
import alphafillPush from './alphafill_push.wgsl?raw';
import bbox from './bbox.wgsl?raw';
import blur from './blur.wgsl?raw';
import combine from './combine.wgsl?raw';
import common from './common.wgsl?raw';
import compose from './compose.wgsl?raw';
import display from './display.wgsl?raw';
import fill from './fill.wgsl?raw';
import guidedApply from './guided_apply.wgsl?raw';
import guidedCoeff from './guided_coeff.wgsl?raw';
import guidedMoments from './guided_moments.wgsl?raw';
import islandsApply from './islands_apply.wgsl?raw';
import islandsCount from './islands_count.wgsl?raw';
import islandsInit from './islands_init.wgsl?raw';
import islandsPropagate from './islands_propagate.wgsl?raw';
import matteFinish from './matte_finish.wgsl?raw';
import matteNormalize from './matte_normalize.wgsl?raw';
import morphology from './morphology.wgsl?raw';
import paint from './paint.wgsl?raw';
import quantize from './quantize.wgsl?raw';
import reduceMinmax from './reduce_minmax.wgsl?raw';
import resample from './resample.wgsl?raw';
import sharpen from './sharpen.wgsl?raw';
import tensorPack from './tensor_pack.wgsl?raw';
import tensorUnpack from './tensor_unpack.wgsl?raw';

/** Every WGSL module, keyed by the name kernels reference. */
export const SHADER_SOURCES: Record<string, string> = {
  'alphafill_pull.wgsl': alphafillPull,
  'alphafill_push.wgsl': alphafillPush,
  'bbox.wgsl': bbox,
  'blur.wgsl': blur,
  'combine.wgsl': combine,
  'common.wgsl': common,
  'compose.wgsl': compose,
  'display.wgsl': display,
  'fill.wgsl': fill,
  'guided_apply.wgsl': guidedApply,
  'guided_coeff.wgsl': guidedCoeff,
  'guided_moments.wgsl': guidedMoments,
  'islands_apply.wgsl': islandsApply,
  'islands_count.wgsl': islandsCount,
  'islands_init.wgsl': islandsInit,
  'islands_propagate.wgsl': islandsPropagate,
  'matte_finish.wgsl': matteFinish,
  'matte_normalize.wgsl': matteNormalize,
  'morphology.wgsl': morphology,
  'paint.wgsl': paint,
  'quantize.wgsl': quantize,
  'reduce_minmax.wgsl': reduceMinmax,
  'resample.wgsl': resample,
  'sharpen.wgsl': sharpen,
  'tensor_pack.wgsl': tensorPack,
  'tensor_unpack.wgsl': tensorUnpack,
};

/**
 * Modules that take `${...}` template parameters, and the values the test suite
 * should compile them with. Kept next to the sources so a new templated shader
 * fails the shader test until it is listed here.
 */
export const TEMPLATED_MODULES: Record<string, Record<string, string>[]> = {
  'resample.wgsl': [
    { DST_FORMAT: 'rgba8unorm' },
    { DST_FORMAT: 'rgba16float' },
    { DST_FORMAT: 'r32float' },
  ],
  'blur.wgsl': [
    { DST_FORMAT: 'rgba32float' },
    { DST_FORMAT: 'rg32float' },
    { DST_FORMAT: 'r32float' },
    { DST_FORMAT: 'rgba16float' },
  ],
  'combine.wgsl': [{ DST_FORMAT: 'rgba16float' }, { DST_FORMAT: 'rgba8unorm' }],
  'fill.wgsl': [
    { DST_FORMAT: 'rgba16float', TEXEL: 'vec4f' },
    { DST_FORMAT: 'r32float', TEXEL: 'vec4f' },
    { DST_FORMAT: 'r32uint', TEXEL: 'vec4u' },
  ],
  'matte_finish.wgsl': [{ DST_FORMAT: 'rgba16float' }, { DST_FORMAT: 'rgba8unorm' }],
  'quantize.wgsl': [{ DST_FORMAT: 'rgba16float' }, { DST_FORMAT: 'rgba8unorm' }],
  'sharpen.wgsl': [{ DST_FORMAT: 'rgba16float' }, { DST_FORMAT: 'rgba8unorm' }],
  'tensor_pack.wgsl': [{}],
  'tensor_unpack.wgsl': [
    { DST_FORMAT: 'rgba16float' },
    { DST_FORMAT: 'r32float' },
    { DST_FORMAT: 'rgba8unorm' },
  ],
};
